import React, { useState, useEffect } from 'react';
import { Grid, Paper, Typography, Card, CardHeader, CardContent, Chip, Box, LinearProgress, Alert } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Pie, Line, Doughnut } from 'react-chartjs-2';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ReceiptIcon from '@mui/icons-material/Receipt';
import axios from 'axios';
import { getAuthToken, addAuthHeader } from '../../../utils/auth';
import { getCurrentUser } from '../../../services/auth';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Set default chart options
const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
    },
  },
};

// Styled components
const DashboardContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: '#F5F5F5',
  minHeight: '100vh',
}));

const MetricCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
}));

const ChartCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
  borderRadius: '8px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  height: '100%',
}));

const DataTable = styled(Table)(({ theme }) => ({
  '& .MuiTableCell-root': {
    padding: '0.5rem 1rem',
    height: '32px',
    fontSize: '0.875rem',
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    backgroundColor: '#064d51',
    color: '#ffffff',
    fontWeight: 600,
  },
  '& .MuiTableRow-root:nth-of-type(even)': {
    backgroundColor: 'rgba(192, 242, 243, 0.1)',
  },
}));

const MetricValue = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 600,
  color: '#064d51',
  marginBottom: theme.spacing(1),
}));

const MetricLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
}));

const TrendIndicator = styled(Box)(({ theme, trend }) => ({
  display: 'flex',
  alignItems: 'center',
  color: trend === 'up' ? '#27ae60' : '#e74c3c',
  fontSize: '0.75rem',
  marginTop: theme.spacing(0.5),
}));

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-AE', {
    style: 'currency',
    currency: 'AED'
  }).format(amount || 0);
};

// Check if we're in development mode
const isDevelopmentMode = false;

// Mock data for development
const mockFinancialData = {
  // Wallet Information
  walletBalance: 45000.00,
  pendingTransactions: 12000.00,
  availableBalance: 33000.00,
  
  // Invoice Status in two formats for flexibility
  invoiceStatus: {
    paid: 24,
    pending: 8,
    overdue: 3
  },
  
  // Pre-formatted for charts
  invoiceByStatus: [
    { status: 'Paid', amount: 24, color: '#4caf50' },
    { status: 'Pending', amount: 8, color: '#ff9800' },
    { status: 'Overdue', amount: 3, color: '#f44336' }
  ],
  
  // Service Expense Breakdown
  serviceExpenses: [
    { service: 'Visa Processing', amount: 25000.00, percentage: 40, color: '#2196f3' },
    { service: 'Document Attestation', amount: 15000.00, percentage: 24, color: '#4caf50' },
    { service: 'Medical Services', amount: 10000.00, percentage: 16, color: '#ff9800' },
    { service: 'Legal Consultation', amount: 8000.00, percentage: 12, color: '#f44336' },
    { service: 'Training Programs', amount: 5000.00, percentage: 8, color: '#9c27b0' }
  ],
  
  // Fee Type Analysis
  feeTypeAnalysis: [
    { type: 'Government Fees', amount: 35000.00, percentage: 56 },
    { type: 'Service Fees', amount: 20000.00, percentage: 32 },
    { type: 'Taxes', amount: 7500.00, percentage: 12 }
  ],
  
  // Recent Invoices
  recentInvoices: [
    { id: 'INV-2025-1234', date: '2025-05-15', description: 'Visa Processing - New Employees', amount: 8500.00, status: 'Paid' },
    { id: 'INV-2025-1235', date: '2025-05-18', description: 'Document Attestation Services', amount: 3200.00, status: 'Paid' },
    { id: 'INV-2025-1236', date: '2025-05-20', description: 'Medical Check-up Services', amount: 4500.00, status: 'Pending' },
    { id: 'INV-2025-1237', date: '2025-05-23', description: 'Legal Consultation - Contract Review', amount: 2800.00, status: 'Pending' },
    { id: 'INV-2025-1238', date: '2025-05-12', description: 'Training Program - Compliance', amount: 1500.00, status: 'Overdue' }
  ],
  
  // Upcoming Payments
  upcomingPayments: [
    { id: 'PAY-2025-432', dueDate: '2025-06-05', description: 'Visa Renewal - Executive Staff', amount: 7200.00 },
    { id: 'PAY-2025-433', dueDate: '2025-06-10', description: 'Annual Compliance Certificate', amount: 4800.00 },
    { id: 'PAY-2025-434', dueDate: '2025-06-15', description: 'Work Permit Processing', amount: 6500.00 }
  ],
  
  // Invoice Summary for metrics
  invoiceSummary: {
    totalPaid: 28500.00,
    totalPending: 7300.00,
    totalOverdue: 1500.00,
    totalOutstanding: 8800.00
  },
  
  // Expense Breakdown for bar chart
  expenseBreakdown: {
    governmentFees: 35000.00,
    serviceFees: 20000.00,
    taxes: 7500.00
  },
  // Monthly Expense Trend for line chart (added to prevent undefined access)
  monthlyExpenseTrend: {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May'],
    datasets: [
      {
        label: 'Monthly Expenses',
        data: [5200, 7400, 4300, 8200, 9800],
        borderColor: '#064d51',
        backgroundColor: 'rgba(6, 77, 81, 0.1)',
        tension: 0.4,
      }
    ]
  }
};

// API integration function to fetch financial dashboard data
const fetchFinancialDashboardData = async () => {
  if (isDevelopmentMode) {
    console.log('[FinancialDashboard] Development mode - returning mock data');
    return mockFinancialData;
  }
  
  try {
    console.log('[FinancialDashboard] Fetching financial dashboard data...');
    const response = await axios.get('/api/dashboard/financial', addAuthHeader());
    console.log('[FinancialDashboard] Financial data fetched successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('[FinancialDashboard] Error fetching financial dashboard data:', error);
    console.error('[FinancialDashboard] Error details:', error.response?.data || error.message);
    // Fallback to empty/zero data on API error
    return {
      walletBalance: 0,
      pendingCharges: 0,
      pendingTransactions: 0,
      availableBalance: 0,
      invoiceStatus: { paid: 0, pending: 0, overdue: 0 },
      invoiceByStatus: [
        { status: 'Paid', amount: 0, color: '#4caf50' },
        { status: 'Pending', amount: 0, color: '#ff9800' },
        { status: 'Overdue', amount: 0, color: '#f44336' }
      ],
      serviceExpenses: [],
      feeTypeAnalysis: [],
      recentInvoices: [],
      upcomingPayments: [],
      invoiceSummary: {
        totalPaid: 0,
        totalPending: 0,
        totalOverdue: 0,
        totalOutstanding: 0
      },
      expenseBreakdown: {
        governmentFees: 0,
        serviceFees: 0,
        taxes: 0
      },
      monthlyExpenseTrend: {
        labels: [],
        datasets: [
          {
            label: 'Monthly Expenses',
            data: [],
            borderColor: '#064d51',
            backgroundColor: 'rgba(6, 77, 81, 0.1)',
            tension: 0.4,
          }
        ]
      }
    };
  }
};

// Chart configurations
const getFinancialDashboardCharts = (data) => {
  // Handle potential missing data structure
  if (!data) {
    console.error('[FinancialDashboard] No data provided to getFinancialDashboardCharts');
    return {
      invoiceStatusChart: null,
      serviceExpensesChart: null,
      expenseBreakdownChart: null
    };
  }
  
  console.log('[FinancialDashboard] Data for charts:', data);

  // Convert invoice status object to array format for chart if needed
  const invoiceByStatus = data.invoiceByStatus || [
    { status: 'Paid', amount: data.invoiceStatus?.paid || 0, color: '#4caf50' },
    { status: 'Pending', amount: data.invoiceStatus?.pending || 0, color: '#ff9800' },
    { status: 'Overdue', amount: data.invoiceStatus?.overdue || 0, color: '#f44336' }
  ];

  // Add colors to service expenses if needed
  const serviceExpenses = data.serviceExpenses?.map((item, index) => ({
    ...item,
    color: item.color || [
      '#2196f3', '#4caf50', '#ff9800', '#f44336', '#9c27b0', '#e91e63'
    ][index % 6]
  })) || [];

  console.log('[FinancialDashboard] Preparing chart data:', { invoiceByStatus, serviceExpenses });

  return {
    invoiceStatusChart: {
      labels: invoiceByStatus.map(item => item.status),
      datasets: [
        {
          data: invoiceByStatus.map(item => item.amount),
          backgroundColor: invoiceByStatus.map(item => item.color),
          borderWidth: 0,
        }
      ]
    },
    serviceExpensesChart: {
      labels: serviceExpenses.map(item => item.service),
      datasets: [
        {
          data: serviceExpenses.map(item => item.amount),
          backgroundColor: serviceExpenses.map(item => item.color || '#2196f3'),
          borderWidth: 0,
        }
      ]
    },
    expenseBreakdownChart: {
      labels: ['Government Fees', 'Service Fees', 'Taxes'],
      datasets: [
        {
          label: 'Expenses',
          data: [
            data.expenseBreakdown?.governmentFees || data.feeTypeAnalysis?.[0]?.amount || 0,
            data.expenseBreakdown?.serviceFees || data.feeTypeAnalysis?.[1]?.amount || 0,
            data.expenseBreakdown?.taxes || data.feeTypeAnalysis?.[2]?.amount || 0
          ],
          backgroundColor: ['#064d51', '#3498db', '#f39c12'],
          borderWidth: 0,
        }
      ]
    }
  };
};

// Dashboard component
const FinancialDashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [charts, setCharts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [userOrganization, setUserOrganization] = useState(null);
  const [authorized, setAuthorized] = useState(false);
  
  // Check user authorization on component mount
  useEffect(() => {
    console.log('[FinancialDashboard] Component mounted');
    
    const loadDashboardData = async () => {
      try {
        console.log('[FinancialDashboard] Loading dashboard data...');
        setLoading(true);
        
        if (isDevelopmentMode) {
          // In development mode, bypass authentication completely
          console.log('[FinancialDashboard] Running in DEVELOPMENT mode - using mock data');
          
          // Create org-specific mock data if needed
          let customizedMockData = {...mockFinancialData};
          
          // Check if a specific organization ID is set in localStorage or URL params
          const urlParams = new URLSearchParams(window.location.search);
          const orgIdFromUrl = urlParams.get('orgId');
          const orgIdFromStorage = localStorage.getItem('selectedOrganizationId');
          const orgId = orgIdFromUrl || orgIdFromStorage;
          
          console.log('[FinancialDashboard] Organization ID detected:', orgId);
          
          // If org-customer-3 is detected, customize mock data
          if (orgId === 'org-customer-3' || window.location.href.includes('org-customer-3')) {
            console.log('[FinancialDashboard] Customizing mock data for org-customer-3');
            // Adjust wallet balance for org-customer-3
            customizedMockData.walletBalance = 55000.00;
            customizedMockData.availableBalance = 43000.00;
            
            // Customize invoice status
            customizedMockData.invoiceStatus.paid = 32;
            customizedMockData.invoiceStatus.pending = 5;
          }
          
          setDashboardData(customizedMockData);
          setCharts(getFinancialDashboardCharts(customizedMockData));
          setAuthorized(true);
          setError(null);
          setLoading(false);
          return;
        }
        
        // Get current user and their organization info
        console.log('[FinancialDashboard] Fetching current user information...');
        const currentUser = await getCurrentUser();
        console.log('[FinancialDashboard] Current user:', currentUser);
        setUser(currentUser);
        
        if (currentUser && currentUser.organization) {
          console.log('[FinancialDashboard] Organization information found:', currentUser.organization);
          setUserOrganization(currentUser.organization);
          
          // Check if user is a customer organization admin
          const isCustomerOrgAdmin = 
            currentUser.organization.type === 'CUSTOMER' && 
            currentUser.roles.some(role => role.includes('ADMIN'));
          
          console.log('[FinancialDashboard] Is user authorized:', isCustomerOrgAdmin);
          console.log('[FinancialDashboard] Organization type:', currentUser.organization.type);
          console.log('[FinancialDashboard] User roles:', currentUser.roles);
          
          setAuthorized(isCustomerOrgAdmin);
          
          if (isCustomerOrgAdmin) {
            console.log('[FinancialDashboard] User is authorized, loading financial data...');
            // Load dashboard data only if user is authorized
            const data = await fetchFinancialDashboardData();
            console.log('[FinancialDashboard] Financial data loaded:', data);
            setDashboardData(data);
            setCharts(getFinancialDashboardCharts(data));
            setError(null);
          } else {
            console.warn('[FinancialDashboard] User is not a customer organization admin');
          }
        } else {
          console.warn('[FinancialDashboard] User organization information not available');
          console.log('[FinancialDashboard] User details:', {
            hasUser: !!currentUser,
            hasOrganization: !!(currentUser && currentUser.organization)
          });
          setAuthorized(false);
          setError('User organization information not available');
        }
      } catch (error) {
        console.error('[FinancialDashboard] Error checking authorization or loading data:', error);
        console.error('[FinancialDashboard] Error details:', error.response?.data || error.message);
        setError('Failed to load financial data. Please try again later.');
      } finally {
        console.log('[FinancialDashboard] Finished loading. Setting loading state to false');
        setLoading(false);
      }
    };
    
    loadDashboardData();
  }, [navigate]);
  
  if (loading) {
    console.log('[FinancialDashboard] Rendering loading state');
    return (
      <DashboardContainer>
        {/* DashboardSelector removed per design */}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <Typography>Loading financial dashboard data...</Typography>
        </Box>
      </DashboardContainer>
    );
  }
  
  // Handle unauthorized access
  if (!authorized && !loading && !isDevelopmentMode) {
    console.log('[FinancialDashboard] Rendering unauthorized state');
    return (
      <DashboardContainer>
        {/* DashboardSelector removed per design */}
        <Box sx={{ p: 3 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body1">
              Access to the Financial Dashboard is restricted to Customer Organization Administrators.
            </Typography>
          </Alert>
          <Typography variant="body2">
            If you believe you should have access to this dashboard, please contact your system administrator.
          </Typography>
        </Box>
      </DashboardContainer>
    );
  }
  
  if (error) {
    console.log('[FinancialDashboard] Rendering error state:', error);
    return (
      <DashboardContainer>
        {/* DashboardSelector removed per design */}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <Typography color="error">{error}</Typography>
        </Box>
      </DashboardContainer>
    );
  }
  
  if (!dashboardData) {
    console.log('[FinancialDashboard] Rendering no data state');
    return (
      <DashboardContainer>
        {/* DashboardSelector removed per design */}
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <Typography>No financial data available.</Typography>
        </Box>
      </DashboardContainer>
    );
  }
  
  console.log('[FinancialDashboard] Rendering financial dashboard with data');
  return (
    <DashboardContainer>
      {/* DashboardSelector removed per design */}
      {/* {isDevelopmentMode && (
        <Alert severity="info" sx={{ m: 2 }}>
          <Typography variant="body2">
            <strong>Development Mode:</strong> Authentication has been bypassed. In production, this dashboard is restricted to Customer Organization Administrators.
          </Typography>
        </Alert>
      )} */}
      <Typography variant="h5" sx={{ my: 2, color: '#064d51', fontWeight: 600 }}>
        Financial Dashboard
      </Typography>
      
      {/* Top Section - Key Metrics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <MetricValue>{formatCurrency(dashboardData.walletBalance)}</MetricValue>
              <AccountBalanceWalletIcon sx={{ color: '#064d51', ml: 1 }} />
            </Box>
            <MetricLabel>Wallet Balance</MetricLabel>
            <TrendIndicator trend="up">
              <ArrowUpwardIcon fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="caption">5% from last month</Typography>
            </TrendIndicator>
          </MetricCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard>
            <MetricValue>{formatCurrency(dashboardData.pendingCharges)}</MetricValue>
            <MetricLabel>Pending Charges</MetricLabel>
            <TrendIndicator trend="down">
              <ArrowDownwardIcon fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="caption">3% from last month</Typography>
            </TrendIndicator>
          </MetricCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <MetricValue>{formatCurrency(dashboardData.invoiceSummary.totalPaid)}</MetricValue>
              <ReceiptIcon sx={{ color: '#27ae60', ml: 1 }} />
            </Box>
            <MetricLabel>Total Paid (Last 90 Days)</MetricLabel>
            <TrendIndicator trend="up">
              <ArrowUpwardIcon fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="caption">12% from previous period</Typography>
            </TrendIndicator>
          </MetricCard>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard>
            <MetricValue>{formatCurrency(dashboardData.invoiceSummary.totalPending + dashboardData.invoiceSummary.totalOverdue)}</MetricValue>
            <MetricLabel>Outstanding Balance</MetricLabel>
            <TrendIndicator trend="down">
              <ArrowDownwardIcon fontSize="small" sx={{ mr: 0.5 }} />
              <Typography variant="caption">8% from last month</Typography>
            </TrendIndicator>
          </MetricCard>
        </Grid>
      </Grid>
      
      {/* Middle Section - Charts */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <ChartCard>
            <CardHeader
              title="Monthly Expense Trend"
              titleTypographyProps={{ variant: 'subtitle1', color: '#064d51' }}
              sx={{ pb: 0 }}
            />
            <CardContent>
              <Line
                data={dashboardData.monthlyExpenseTrend}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false,
                    },
                    tooltip: {
                      callbacks: {
                        label: function(context) {
                          return formatCurrency(context.raw);
                        }
                      }
                    }
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        callback: function(value) {
                          return formatCurrency(value);
                        }
                      }
                    },
                  },
                }}
                height={200}
              />
            </CardContent>
          </ChartCard>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <ChartCard>
            <CardHeader
              title="Invoice Status"
              titleTypographyProps={{ variant: 'subtitle1', color: '#064d51' }}
              sx={{ pb: 0 }}
            />
            <CardContent>
              {charts.invoiceStatusChart && (
                <Box sx={{ height: 200 }}>
                  <Doughnut
                    data={charts.invoiceStatusChart}
                    options={{
                      ...chartOptions,
                      cutout: '60%',
                      plugins: {
                        ...chartOptions.plugins,
                        legend: {
                          position: 'bottom',
                          labels: {
                            boxWidth: 12,
                            padding: 8,
                          }
                        }
                      }
                    }}
                  />
                </Box>
              )}
            </CardContent>
          </ChartCard>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <ChartCard>
            <CardHeader
              title="Service Expenses"
              titleTypographyProps={{ variant: 'subtitle1', color: '#064d51' }}
              sx={{ pb: 0 }}
            />
            <CardContent>
              {charts.serviceExpensesChart && (
                <Box sx={{ height: 180 }}>
                  <Pie
                    data={charts.serviceExpensesChart}
                    options={{
                      ...chartOptions,
                      plugins: {
                        ...chartOptions.plugins,
                        legend: {
                          position: 'right',
                          labels: {
                            boxWidth: 12,
                            padding: 10,
                          },
                        },
                        tooltip: {
                          callbacks: {
                            label: function(context) {
                              return `${context.label}: ${formatCurrency(context.raw)}`;
                            }
                          }
                        }
                      },
                    }}
                  />
                </Box>
              )}
            </CardContent>
          </ChartCard>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <ChartCard>
            <CardHeader
              title="Expense Breakdown"
              titleTypographyProps={{ variant: 'subtitle1', color: '#064d51' }}
              sx={{ pb: 0 }}
            />
            <CardContent>
              {charts && charts.expenseBreakdownChart && (
                <Bar
                  data={charts.expenseBreakdownChart}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'top',
                      },
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            return `${context.dataset.label}: ${formatCurrency(context.raw)}`;
                          }
                        }
                      }
                    },
                    scales: {
                      x: {
                        stacked: true,
                      },
                      y: {
                        stacked: true,
                        ticks: {
                          callback: function(value) {
                            return formatCurrency(value);
                          }
                        }
                      }
                    }
                  }}
                  height={180}
                />
              )}
            </CardContent>
          </ChartCard>
        </Grid>
      </Grid>
      
      {/* Bottom Section - Tables */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <ChartCard>
            <CardHeader
              title="Recent Invoices"
              titleTypographyProps={{ variant: 'subtitle1', color: '#064d51' }}
              sx={{ pb: 0 }}
            />
            <CardContent>
              <TableContainer>
                <DataTable size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Invoice #</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Service</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dashboardData.recentInvoices.map((invoice, index) => (
                      <TableRow key={index}>
                        <TableCell>{invoice.invoiceNumber}</TableCell>
                        <TableCell>{invoice.date}</TableCell>
                        <TableCell>{invoice.service}</TableCell>
                        <TableCell align="right">{formatCurrency(invoice.amount)}</TableCell>
                        <TableCell>
                          <Chip 
                            label={invoice.status} 
                            size="small"
                            sx={{ 
                              backgroundColor: 
                                invoice.status === 'Paid' ? '#27ae60' :
                                invoice.status === 'Pending' ? '#f39c12' : '#e74c3c',
                              color: 'white',
                              fontWeight: 500,
                              fontSize: '0.7rem',
                            }} 
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </DataTable>
              </TableContainer>
            </CardContent>
          </ChartCard>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <ChartCard>
            <CardHeader
              title="Upcoming Payments"
              titleTypographyProps={{ variant: 'subtitle1', color: '#064d51' }}
              sx={{ pb: 0 }}
            />
            <CardContent>
              <TableContainer>
                <DataTable size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Invoice #</TableCell>
                      <TableCell>Due Date</TableCell>
                      <TableCell>Service</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="right">Days Left</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dashboardData.upcomingPayments.map((payment, index) => (
                      <TableRow key={index}>
                        <TableCell>{payment.invoiceNumber}</TableCell>
                        <TableCell>{payment.dueDate}</TableCell>
                        <TableCell>{payment.service}</TableCell>
                        <TableCell align="right">{formatCurrency(payment.amount)}</TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={payment.daysRemaining} 
                            size="small"
                            sx={{ 
                              backgroundColor: payment.daysRemaining <= 3 ? '#e74c3c' : 
                                              payment.daysRemaining <= 7 ? '#f39c12' : '#27ae60',
                              color: 'white',
                              fontWeight: 500,
                              minWidth: '32px'
                            }} 
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </DataTable>
              </TableContainer>
            </CardContent>
          </ChartCard>
        </Grid>
      </Grid>
    </DashboardContainer>
  );
};

export default FinancialDashboard;